export type BlockType = "paragraph" | "heading";

export interface BlockAttributes {
  content: string;
}

export interface Block {
  id: string;
  type: BlockType;
  attributes: BlockAttributes;
}

export class EditorState {
  private _blocks: Block[] = [];
  private _activeBlockId: string | null = null;

  get blocks(): Block[] {
    return this._blocks;
  }

  get activeBlockId(): string | null {
    return this._activeBlockId;
  }

  set blocks(value: Block[]) {
    this._blocks = value;
  }

  set activeBlockId(id: string | null) {
    this._activeBlockId = id;
  }

  createBlockId(): string {
    return "b_" + Math.random().toString(36).slice(2, 10);
  }

  insertBlock(type: BlockType, content = "", index?: number): Block {
    const block: Block = {
      id: this.createBlockId(),
      type,
      attributes: { content }
    };

    if (index === undefined || index < 0 || index > this._blocks.length) {
      this._blocks.push(block);
    } else {
      this._blocks.splice(index, 0, block);
    }

    this._activeBlockId = block.id;
    return block;
  }

  updateBlock(id: string, attrs: Partial<BlockAttributes>): void {
    const block = this._blocks.find((b) => b.id === id);
    if (!block) return;
    block.attributes = { ...block.attributes, ...attrs };
  }

  removeBlock(id: string): void {
    const idx = this._blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    this._blocks.splice(idx, 1);
    if (this._activeBlockId === id) {
      this._activeBlockId =
        this._blocks[idx]?.id || this._blocks[idx - 1]?.id || null;
    }
  }

  moveBlock(id: string, direction: "up" | "down"): void {
    const idx = this._blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    const newIndex = direction === "up" ? idx - 1 : idx + 1;
    if (newIndex < 0 || newIndex >= this._blocks.length) return;
    const [block] = this._blocks.splice(idx, 1);
    this._blocks.splice(newIndex, 0, block);
  }

  toJSON(): Block[] {
    return JSON.parse(JSON.stringify(this._blocks));
  }

  toHTML(): string {
    return this._blocks
      .map((block) => {
        const escaped = (block.attributes.content || "")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        if (block.type === "heading") {
          return `<h2>${escaped}</h2>`;
        }
        return `<p>${escaped}</p>`;
      })
      .join("\n");
  }
}
